<template>
  <div class="household-details" redesign>
    <!-- Request stepper -->
    <RequestStepProgressBar isHouseholdDetailsSurvey />

    <div class="form-container household-details-form">
      <!-- On-boarding survey callout -->
      <div class="form-field info-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/information-icon.png" />
        </div>
        <div class="tooltip">
          <span class="tooltip-text" v-translate>
            We ask the following questions to ensure that we are holding ourselves accountable to our mission of equitable and unbiased grantmaking. The grant manager who reviews your request never sees these answers, and your responses will not impact your chances for approval in any way.
          </span>
        </div>
      </div>

      <div class="survey-container">
        <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
        <SurveyResponseComponent
          v-else
          @survey-done="onSurveyDone"
          :surveyResponse="surveyResponse"
          :nextStep="nextStep"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SurveyResponse from '@/services/surveyresponses'
import SurveyResponseComponent from '@/components/SurveyResponse'
import { mapState } from 'vuex'
import LoadSpinner from '@/components/LoadSpinner'
import InfoBox from '@/components/InfoBox'
import UserEvent from '@/services/userEvents'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'

export default {
  name: 'HouseholdDetailsRequest',
  components: {
    InfoBox,
    SurveyResponseComponent,
    LoadSpinner,
    RequestStepProgressBar,
  },
  data() {
    const programName =
      (this.organizationDetails && this.organizationDetails.programName) || 'Grant Circles'
    return {
      loading: null,
      surveyResponse: null,
      surveyName: 'Household Details',
      content:
        this.$gettext('To complete your') +
        ' ' +
        programName +
        ' ' +
        this.$gettext(
          'profile, answer the questions below. The following information is requested for reporting purposes and does not impact your eligibility for a grant.',
        ),
      hasCompletedFinancialHealthSurvey: false,
    }
  },
  async created() {
    this.loading = true
    const isComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, this.surveyName)

    if (isComplete && this.organizationDetails.isFinancialHealthSurveyEnabled) {
      return this.$router.push({ name: 'FinancialHealthRequest' })
    }

    if (isComplete && !this.organizationDetails.isFinancialHealthSurveyEnabled) {
      return this.$router.push({ name: 'PayPalVerify' })
    }

    // Otherwise, pull in a survey for them to complete
    const surveyResponse = await SurveyResponse.getOrCreate(this.user, this.surveyName)
    this.surveyResponse = surveyResponse

    this.hasCompletedFinancialHealthSurvey = await SurveyResponse.checkIfEmployeeHasCompleted(
      this.user,
      'Financial Health',
    )

    this.loading = false
  },
  mounted() {
    // Update the current request completion status
    UserEvent.api.requestUpdateCompletionStatus(
      this.currentRequest.id,
      UserEvent.options.REQUEST_STEP_HOUSEHOLD_DETAILS,
    )
  },
  methods: {
    onSurveyDone(success) {
      if (success && this.organizationDetails.isFinancialHealthSurveyEnabled) {
        return this.$router.push({ name: 'FinancialHealthRequest' })
      }

      if (success && !this.organizationDetails.isFinancialHealthSurveyEnabled) {
        return this.$router.push({ name: 'PayPalVerify' })
      }

      this.$Alert.alert({
        type: 'error',
        message: `<h4>Something went wrong!</h4><p>Please try again later.</p>`,
        timeout: 6000,
      })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentRequest']),
    nextStep() {
      if(this.organizationDetails.isFinancialHealthSurveyEnabled && !this.hasCompletedFinancialHealthSurvey) {
        return "Financial Health Survey"
      }

      return "Request Summary"
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.household-details {
  display: flex;
  flex-direction: column;

  .household-details-form {
    @extend .constrain-tablet-and-up;
  }
}
</style>
